import React, { useState, useMemo } from "react";
import { X } from "lucide-react";
import { SectionTitle } from ".";
import { useUserContext } from "../../../../contexts/UserContext";
import { permission } from "../../../../config/permission";
import useAutoSave from "../useAutoSave";
import { EPM_LIMITS } from "../../validation/epmRules";
import { evidenceItemSchema } from "../../validation/epmSchemas";
import Button from "../../../UI/Button/Button";
import { Input } from "../../../UI";

const MAX_EVIDENCE = EPM_LIMITS.EVIDENCE_MAX_COUNT;

// Evidence is stored as an array of strings. Older tests stored it keyed by
// type ({ [type]: text } or [{ [type]: text }]); read those as their values.
const toEvidenceList = (evidence) => {
    if (!evidence) return [];
    const items = Array.isArray(evidence) ? evidence : Object.values(evidence);
    return items
        .flatMap((item) => (item && typeof item === "object" ? Object.values(item) : [item]))
        .filter((val) => typeof val === "string" && val.trim().length > 0);
};

const EvidenceSection = ({ testData, setTestData }) => {
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);

    const evidenceList = useMemo(
        () => toEvidenceList(testData?.ideation?.evidence),
        [testData?.ideation?.evidence],
    );

    const [evidenceValue, setEvidenceValue] = useState("");
    const [inputError, setInputError] = useState("");
    const { saveNow } = useAutoSave(testData);

    const saveEvidence = (updatedEvidence) => {
        if (!canUpdate) return;
        if (setTestData) {
            setTestData((prev) => ({
                ...prev,
                ideation: {
                    ...prev?.ideation,
                    evidence: updatedEvidence,
                },
            }));
        }
        saveNow({ ideation: { evidence: updatedEvidence } });
    };

    const handleAdd = (e) => {
        e?.preventDefault();
        const text = evidenceValue.trim();
        setInputError("");

        if (!text) return;

        try {
            evidenceItemSchema.validateSync(text);
        } catch (validationErr) {
            setInputError(validationErr?.message || "Invalid evidence item.");
            return;
        }

        if (evidenceList.length >= MAX_EVIDENCE) return;

        saveEvidence([...evidenceList, text]);
        setEvidenceValue("");
    };

    const handleRemove = (indexToRemove) => {
        saveEvidence(evidenceList.filter((_, index) => index !== indexToRemove));
    };

    return (
        <section className="epm-idea__card" id="epm-field-evidence">
            <SectionTitle>Evidence</SectionTitle>

            {canUpdate && (
                evidenceList.length < MAX_EVIDENCE ? (
                    <div>
                        <div className="flex items-center gap-2">
                            <Input
                                placeholder="Paste a link or name the source — e.g. Adobe Analytics mobile PDP flow"
                                value={evidenceValue}
                                onChange={(e) => {
                                    setEvidenceValue(e.target.value);
                                    if (inputError) setInputError("");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAdd();
                                    }
                                }}
                                aria-label="Evidence source or link"
                                error={inputError}
                                className="flex-1"
                            />
                            <Button
                                variant="secondary"
                                size="sm"
                                type="button"
                                onClick={handleAdd}
                                disabled={!evidenceValue.trim()}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 font-medium">
                        All evidence added ({MAX_EVIDENCE}/{MAX_EVIDENCE} maximum reached).
                    </div>
                )
            )}

            {/* Selected Evidence List */}
            {evidenceList.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                    {evidenceList.map((value, index) => (
                        <div
                            key={`${index}-${value}`}
                            className="stride-subtle-box flex items-center justify-between text-xs hover:bg-slate-100/70 transition-all"
                        >
                            <span className="text-stride-body truncate font-normal min-w-0 pr-2">
                                {value}
                            </span>
                            {canUpdate && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(index)}
                                    className="text-stride-muted-light hover:text-red-500 transition-colors p-1 flex-none cursor-pointer bg-transparent border-0"
                                    title="Remove evidence"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <p className="epm-idea__note">
                {evidenceList.length === 0
                    ? "Optional · up to 3 links or source notes"
                    : `${evidenceList.length} of ${MAX_EVIDENCE} evidence item(s) added`}
            </p>
        </section>
    );
};

export default EvidenceSection;
